import { useEffect, useMemo, useState } from 'react';
import { Edit2, MoreVertical, Settings, ClipboardCheck, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { NoGroupsMessage } from '../../components/NoGroupsMessage';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../../components/ui/Badge';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { Button } from '../../components/ui/Button';
import { getTenantColor } from '../../lib/colors';
import { ApprovalsConfigEditDialog } from '../../components/approvals/ApprovalsConfigEditDialog';
import { ApprovalsGraphApiRequestDialog } from '../../components/approvals/ApprovalsGraphApiRequestDialog';
import { Skeleton } from '../../components/ui/Skeleton';
import { PageHeader } from '../../components/ui/PageHeader';
import { TenantLogo } from '../../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../../hooks/useTenantMetaMap';

interface TenantApprovalsConfig {
  enabled?: boolean;
  microsoft?: {
    enabled?: boolean;
    client_id?: string;
    client_secret?: string;
    tenant_id?: string;
  };
  defaults?: {
    expiration_hours?: number;
    notify_all_by_default?: boolean;
  };
}

export function ApprovalsConfig() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('approvals', 'read');
  const { isAllowed: canConfigure } = usePermission('approvals', 'configure');
  const canApproveGraphApi = canConfigure;
  const tenantMetaMap = useTenantMetaMap(canView);

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const tenantsToQuery = useMemo(() => {
    const list = (selectedTenants && selectedTenants.length > 0) ? selectedTenants : accessibleTenants;
    return (list || []).map((t) => String(t).toLowerCase());
  }, [accessibleTenants, selectedTenants]);

  const [tenants, setTenants] = useState<Array<{ code: string; name: string; color?: string | null; domains?: string[] }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMasterEditor, setShowMasterEditor] = useState(false);

  const [tenantConfigs, setTenantConfigs] = useState<Record<string, TenantApprovalsConfig>>({});
  const [masterConfig, setMasterConfig] = useState<TenantApprovalsConfig | null>(null);
  const [tenantEditorTenant, setTenantEditorTenant] = useState<string | null>(null);
  const [graphApiStatuses, setGraphApiStatuses] = useState<Record<string, any>>({});
  const [graphApiDialogTenant, setGraphApiDialogTenant] = useState<{
    code: string;
    name: string;
    domains?: string[];
    status?: any;
  } | null>(null);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const tenantsResponse = await apiClient.getTenants();
        const tenantsArr = (Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || []) as any[];
        const wanted = new Set((tenantsToQuery || []).map((t) => String(t).toLowerCase()));
        const filtered = tenantsArr
          .map((t: any) => ({ code: String(t.code || '').toLowerCase(), name: t.name, color: t.color, domains: t.domains || [] }))
          .filter((t: any) => wanted.has(String(t.code).toLowerCase()));
        if (!cancelled) setTenants(filtered);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load tenants');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(',')]);

  // Load master config
  useEffect(() => {
    if (!canView) return;
    void (async () => {
      try {
        const res = await apiClient.getApprovalsMasterConfig();
        setMasterConfig(res?.approvals || {});
      } catch {
        // ignore
      }
    })();
  }, [canView]);

  // Background-load configs for tenants in view
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    void (async () => {
      const missing = (tenantsToQuery || []).filter((t) => !tenantConfigs[t]);
      if (missing.length === 0) return;
      try {
        const results = await Promise.all(
          missing.map(async (t) => {
            try {
              const res = await apiClient.getApprovalsTenantConfig(t);
              return { t, cfg: res?.approvals || {} };
            } catch {
              return { t, cfg: null };
            }
          })
        );
        if (cancelled) return;
        setTenantConfigs((prev) => {
          const next = { ...prev };
          for (const r of results) {
            if (r.cfg !== null) next[r.t] = r.cfg;
          }
          return next;
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(','), tenantConfigs]);

  // Background-load Graph API statuses for tenants in view
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    void (async () => {
      const missing = (tenantsToQuery || []).filter((t) => typeof graphApiStatuses[t] === 'undefined');
      if (missing.length === 0) return;
      try {
        const results = await Promise.all(
          missing.map(async (t) => {
            try {
              const res = await apiClient.getApprovalsGraphApiStatus(t);
              return { t, status: res?.graphApi || null };
            } catch {
              return { t, status: null };
            }
          })
        );
        if (cancelled) return;
        setGraphApiStatuses((prev) => {
          const next = { ...prev };
          for (const r of results) next[r.t] = r.status;
          return next;
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(','), graphApiStatuses]);

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Approvals Configuration</CardTitle>
        </CardHeader>
        <CardContent>You don&apos;t have permission to view Approvals Configuration.</CardContent>
      </Card>
    );
  }

  const openTenantEditor = (tenantCode: string) => {
    setTenantEditorTenant(tenantCode);
  };

  const refreshApprovalsGraphApiStatus = async (tenantCode: string) => {
    const tc = String(tenantCode || '').toLowerCase();
    if (!tc) return;
    try {
      const res = await apiClient.getApprovalsGraphApiStatus(tc);
      setGraphApiStatuses((prev) => ({ ...prev, [tc]: res?.graphApi || null }));
    } catch {
      setGraphApiStatuses((prev) => ({ ...prev, [tc]: null }));
    }
  };

  const openGraphApiDialog = async (tenantCode: string, tenantName: string) => {
    const tc = String(tenantCode || '').toLowerCase();
    try {
      const res = await apiClient.getApprovalsGraphApiStatus(tc);
      setGraphApiStatuses((prev) => ({ ...prev, [tc]: res?.graphApi || null }));
      const tenant = tenants.find((t) => String(t.code).toLowerCase() === tc);
      setGraphApiDialogTenant({ code: tc, name: tenantName, domains: tenant?.domains || [], status: res?.graphApi });
    } catch (e: any) {
      const tenant = tenants.find((t) => String(t.code).toLowerCase() === tc);
      setGraphApiDialogTenant({ code: tc, name: tenantName, domains: tenant?.domains || [], status: undefined });
      alert(e?.message || 'Failed to load approvals Graph API status');
    }
  };

  const getConfigStatus = (cfg: TenantApprovalsConfig) => {
    const hasOwnConfig = cfg && Object.keys(cfg).length > 0;
    const isEnabled = cfg?.enabled === true;

    return {
      hasOwnConfig,
      isEnabled,
    };
  };

  const getGraphApiBadge = (status?: any) => {
    if (status?.accessGranted) {
      return (
        <Badge variant="success">
          <CheckCircle className="w-3 h-3 mr-1" />
          Graph API
        </Badge>
      );
    }
    if (status?.pendingRequest) {
      return (
        <Badge variant="warning">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </Badge>
      );
    }
    return (
      <Badge variant="outline">
        <XCircle className="w-3 h-3 mr-1" />
        No Graph
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ClipboardCheck}
        iconColor="text-purple-600 dark:text-purple-400"
        iconBgColor="bg-purple-100 dark:bg-purple-900/30"
        description="Configure Microsoft Approvals integration per tenant"
        count={tenants.length}
        countLabel="tenants"
        countColor="text-purple-600 dark:text-purple-400"
        actions={
          canConfigure ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <DropdownMenu
                className="w-full h-full"
                trigger={
                  <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                    <Settings className="w-4 h-4 mr-2" />
                    Actions
                  </Button>
                }
                items={[
                  {
                    label: 'Edit Approvals Defaults',
                    icon: <Edit2 className="w-4 h-4" />,
                    onClick: () => setShowMasterEditor(true),
                  },
                ]}
              />
            </div>
          ) : undefined
        }
      />

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      {loading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <Skeleton variant="circle" className="w-10 h-10" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tenants.length === 0 && !loading && !error ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No tenants found for current selection.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => {
            const tenantMeta = resolveTenantMeta(tenantMetaMap, tenant.code, tenant);
            const cfg = tenantConfigs[tenant.code] || {};
            const effectiveConfig = cfg && Object.keys(cfg).length > 0
              ? cfg
              : ({
                  defaults: masterConfig?.defaults,
                } as TenantApprovalsConfig);
            const status = getConfigStatus(cfg);
            const graphApiStatus = graphApiStatuses[tenant.code];

            return (
              <Card key={tenant.code} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <TenantLogo
                        tenant={tenantMeta}
                        size="md"
                        rounded="lg"
                        className="border border-gray-200/80 dark:border-gray-700/80"
                      />
                      <div>
                        <CardTitle className="text-lg" style={{ color: getTenantColor(tenantMeta) }}>
                          {tenantMeta.name}
                        </CardTitle>
                        <CardDescription className="font-mono text-xs mt-1">
                          {tenantMeta.code}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const items = [
                          canConfigure
                            ? {
                                label: 'Edit Approvals Config',
                                icon: <Edit2 className="w-4 h-4" />,
                                onClick: () => openTenantEditor(tenant.code),
                              }
                            : null,
                          canConfigure
                            ? {
                                label: graphApiStatus?.pendingRequest
                                  ? 'Approve Graph API Access'
                                  : graphApiStatus?.accessGranted
                                    ? 'View Graph API Status'
                                    : 'Request Graph API Access',
                                icon: <ClipboardCheck className="w-4 h-4" />,
                                onClick: () => openGraphApiDialog(tenant.code, tenant.name),
                              }
                            : null,
                        ].filter(Boolean) as any[];

                        if (items.length === 0) return null;

                        return (
                          <DropdownMenu
                            trigger={
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            }
                            items={items}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {!status.hasOwnConfig ? (
                      <Badge variant="outline" className="text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700">
                        Inherits Master
                      </Badge>
                    ) : (
                      <Badge variant="info">Own Config</Badge>
                    )}

                    {effectiveConfig.enabled ? (
                      <Badge variant="success">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <XCircle className="w-3 h-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                    {getGraphApiBadge(graphApiStatus)}
                  </div>

                  {Array.isArray(tenant.domains) && tenant.domains.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Domains: {tenant.domains.join(', ')}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ApprovalsConfigEditDialog
        open={showMasterEditor}
        onClose={() => setShowMasterEditor(false)}
        onSuccess={async () => {
          try {
            const res = await apiClient.getApprovalsMasterConfig();
            setMasterConfig(res?.approvals || {});
          } catch {
            // ignore
          }
        }}
        isMaster
      />

      <ApprovalsConfigEditDialog
        open={!!tenantEditorTenant}
        tenantCode={tenantEditorTenant}
        onClose={() => setTenantEditorTenant(null)}
        onSuccess={async () => {
          const tc = String(tenantEditorTenant || '').toLowerCase();
          if (!tc) return;
          try {
            const res = await apiClient.getApprovalsTenantConfig(tc);
            setTenantConfigs((prev) => ({ ...prev, [tc]: res?.approvals || {} }));
          } catch {
            // ignore
          }
        }}
      />

      <ApprovalsGraphApiRequestDialog
        open={!!graphApiDialogTenant}
        onClose={() => setGraphApiDialogTenant(null)}
        onSuccess={() => {
          const tc = String(graphApiDialogTenant?.code || '').toLowerCase();
          if (tc) void refreshApprovalsGraphApiStatus(tc);
        }}
        tenantCode={graphApiDialogTenant?.code || null}
        tenantName={graphApiDialogTenant?.name}
        tenantDomains={graphApiDialogTenant?.domains || []}
        currentStatus={graphApiDialogTenant?.code ? graphApiStatuses[graphApiDialogTenant.code] : undefined}
        canApprove={canApproveGraphApi}
      />
    </div>
  );
}
